/**
 * Check Airline Custom: horizontal scroll, optional arrows, optional auto-scroll.
 * Safe if the section appears multiple times (duplicate script tags): listeners register once.
 */
(function () {
  /**
   * scrollWidth - clientWidth can briefly read as 0 during parent reveal (filter/transform) or layout.
   * Fallback: sum flex children + gap vs inner track width.
   */
  function getMaxScrollLeft(el) {
    var native = el.scrollWidth - el.clientWidth;
    if (native > 1) return native;

    var n = el.children && el.children.length;
    if (!n) return Math.max(0, native);

    var cs = window.getComputedStyle(el);
    var gap = parseFloat(cs.columnGap);
    if (isNaN(gap)) gap = parseFloat(cs.gap);
    if (isNaN(gap)) gap = 0;

    var sum = 0;
    for (var i = 0; i < n; i++) {
      sum += el.children[i].offsetWidth;
    }
    sum += gap * Math.max(0, n - 1);

    var padL = parseFloat(cs.paddingLeft) || 0;
    var padR = parseFloat(cs.paddingRight) || 0;
    var inner = el.clientWidth - padL - padR;
    var measured = Math.ceil(sum - inner);

    return Math.max(0, native, measured);
  }

  function parseSpeed(root) {
    var raw = root && root.getAttribute('data-check-airline-speed');
    var parsed = parseInt(raw, 10);
    if (isNaN(parsed) || parsed < 8) return 32;
    if (parsed > 120) return 120;
    return parsed;
  }

  function bind(scrollEl) {
    if (!scrollEl || scrollEl.getAttribute('data-check-airline-bound') === '1') return;

    var root = scrollEl.closest('[data-airline-scroll-root]') || scrollEl.closest('.check-airline');
    if (!root) return;
    var isMarquee = root.getAttribute('data-check-airline-marquee') === 'true';

    var wrap = scrollEl.closest('[data-check-airline-wrap]');
    if (!wrap) return;
    var prevBtn = wrap.querySelector('[data-check-airline-prev]');
    var nextBtn = wrap.querySelector('[data-check-airline-next]');

    scrollEl.setAttribute('data-check-airline-bound', '1');

    var step = 320;
    var pauseUntil = 0;
    var lastTs = 0;

    function updateArrows() {
      if (isMarquee) {
        // Marquee loops forever, so arrows are always available.
        if (prevBtn) prevBtn.classList.remove('check-airline__arrow--hidden');
        if (nextBtn) nextBtn.classList.remove('check-airline__arrow--hidden');
        return;
      }
      var maxScroll = getMaxScrollLeft(scrollEl);
      var atStart = scrollEl.scrollLeft <= 2;
      var atEnd = maxScroll <= 2 || scrollEl.scrollLeft >= maxScroll - 2;
      if (prevBtn) prevBtn.classList.toggle('check-airline__arrow--hidden', atStart);
      if (nextBtn) nextBtn.classList.toggle('check-airline__arrow--hidden', atEnd);

      // Layout helper classes (used only when auto-scroll is OFF; see CSS).
      wrap.classList.toggle('check-airline__scroll-wrap--at-start', atStart);
      wrap.classList.toggle('check-airline__scroll-wrap--at-end', atEnd);
    }

    function bumpArrowPause() {
      pauseUntil = performance.now() + 900;
      lastTs = 0;
    }

    var userScrollTimer = null;
    function pauseMarqueeBriefly() {
      if (!isMarquee) return;
      wrap.classList.add('is-user-scrolling');
      if (userScrollTimer) window.clearTimeout(userScrollTimer);
      userScrollTimer = window.setTimeout(function () {
        wrap.classList.remove('is-user-scrolling');
      }, 900);
    }

    scrollEl.addEventListener('scroll', updateArrows);
    window.addEventListener('resize', updateArrows);
    requestAnimationFrame(function () {
      requestAnimationFrame(updateArrows);
    });
    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () {
        updateArrows();
      });
      ro.observe(scrollEl);
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        bumpArrowPause();
        pauseMarqueeBriefly();
        scrollEl.scrollBy({ left: -step, behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        bumpArrowPause();
        pauseMarqueeBriefly();
        scrollEl.scrollBy({ left: step, behavior: 'smooth' });
      });
    }

    updateArrows();

    var autoEnabled = root.getAttribute('data-check-airline-auto') === 'true';
    var reduceMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // In marquee mode, auto movement is handled by CSS animation.
    if (isMarquee || !autoEnabled || reduceMotion) return;

    var speedPxPerSec = parseSpeed(root);
    var pauseOnHover = root.getAttribute('data-check-airline-pause-hover') === 'true';
    var rafId = null;

    function tick(ts) {
      rafId = requestAnimationFrame(tick);
      if (lastTs === 0) {
        lastTs = ts;
        return;
      }
      var dt = (ts - lastTs) / 1000;
      lastTs = ts;
      if (dt > 0.08) dt = 0.08;

      if (pauseOnHover && wrap.matches && wrap.matches(':hover')) return;
      if (ts < pauseUntil) return;

      var maxLeft = getMaxScrollLeft(scrollEl);
      if (maxLeft < 1) return;

      var next = scrollEl.scrollLeft + speedPxPerSec * dt;
      if (next >= maxLeft - 0.5) {
        scrollEl.scrollLeft = 0;
      } else {
        scrollEl.scrollLeft = next;
      }
    }

    lastTs = 0;
    rafId = requestAnimationFrame(tick);

    scrollEl._checkAirlineCleanup = function () {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
      scrollEl.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
      scrollEl.removeAttribute('data-check-airline-bound');
    };
  }

  function initAll() {
    document.querySelectorAll('[data-check-airline-scroll]').forEach(bind);
  }

  function teardownSection(host) {
    if (!host || !host.querySelectorAll) return;
    host.querySelectorAll('[data-check-airline-scroll]').forEach(function (el) {
      if (typeof el._checkAirlineCleanup === 'function') {
        el._checkAirlineCleanup();
        delete el._checkAirlineCleanup;
      }
    });
  }

  if (!window.__checkAirlineCustomBooted) {
    window.__checkAirlineCustomBooted = true;
    document.addEventListener('shopify:section:load', initAll);
    document.addEventListener('shopify:section:unload', function (e) {
      teardownSection(e.target);
    });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    }
  }

  initAll();
})();
